import { useMutation, useQueryClient } from '@tanstack/react-query';
import ApiService from 'src/services/ApiService';
import { StackFilter } from 'src/services/documents/types';
import { useDocumentViewStore } from 'src/stores/useDocumentViewStore';

export const pendingStackFilterId = '_new_stack_filter';

export const useCloneStackFilterMutation = () => {
  const queryClient = useQueryClient();
  const documentViewStoreActions = useDocumentViewStore(
    (state) => state.actions,
  );
  const currentStackFilterId = useDocumentViewStore(
    (state) => state.currentStackFilterId,
  );
  return useMutation({
    mutationKey: ['/stack-filters' as const],
    mutationFn: ApiService.documents.cloneStackFilter,
    onMutate: async (newStackFilter) => {
      const queryKey: ['/stack-filters'] = ['/stack-filters'];

      // Cancel any outgoing refetches
      // (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries({ queryKey });
      await queryClient.cancelQueries({
        queryKey: ['/stack-filter', pendingStackFilterId],
      });
      // Snapshot the previous value
      const previousStackFilters = queryClient.getQueryData(
        queryKey,
      ) as StackFilter[];

      const newStackFilters = [
        ...previousStackFilters,
        {
          ...newStackFilter.stackFilter,
          id: pendingStackFilterId,
          name: newStackFilter.name,
        },
      ];
      const previousCurrentStackFilterId = currentStackFilterId;

      // Optimistically update to the new value
      queryClient.setQueryData(queryKey, newStackFilters);
      queryClient.setQueryData(['/stack-filter', pendingStackFilterId], {
        documentTypes: [],
        ...queryClient.getQueryData([
          '/stack-filter',
          newStackFilter.stackFilter.id,
        ]),
        id: pendingStackFilterId,
        name: newStackFilter.name,
      });
      documentViewStoreActions.setCurrentStackFilterId(pendingStackFilterId);

      // Return a context with the previous and new values
      return {
        previousStackFilters,
        newStackFilters,
        previousCurrentStackFilterId,
      };
    },
    onError: (_err, newStackFilter, context) => {
      documentViewStoreActions.setPendingState(
        newStackFilter.stackFilter.id,
        false,
      );
      if (context?.previousCurrentStackFilterId) {
        documentViewStoreActions.setCurrentStackFilterId(
          context.previousCurrentStackFilterId,
        );
      }
      queryClient.setQueryData(['/stack-filter', pendingStackFilterId], null);
      queryClient.setQueryData(
        ['/stack-filters'],
        context?.previousStackFilters,
      );
    },
    onSettled: (newStackFilter) => {
      if (newStackFilter) {
        documentViewStoreActions.setCurrentStackFilterId(
          newStackFilter.stackFilter.id,
        );
      }
      queryClient.invalidateQueries({
        queryKey: ['/stack-filters'],
      });
      queryClient.invalidateQueries({
        queryKey: ['/stack-filter', pendingStackFilterId],
      });
    },
  });
};
